import { useState } from "react";
import { AnimatePresence } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/landing/primitives";
import { WaitlistModal } from "@/components/landing/WaitlistModal";
import { Eyebrow, H2, Shot, VERIFY_URL, ALPHA_URL, useVerifyStats } from "./shared";

export const VerifiedRecord = () => {
  const v = useVerifyStats();
  const live = v?.live && v.wr04 != null;
  const since = v?.first_fire ? new Date(v.first_fire).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null;
  const stats = live
    ? [[`${v.wr04.toFixed(1)}%`, "A-grade setups banked profit"], [`${v.wr2.toFixed(1)}%`, "ran to the full 2R target"], [v.resolved.toLocaleString(), "setups resolved"], [String(v.markets), "markets covered"]]
    : null;
  return (
    <section id="verified" data-testid="verified-record" className="border-t border-white/[0.06] bg-[#0A0B0D] py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="grid items-end gap-8 lg:grid-cols-[1.4fr_1fr]">
          <Reveal>
            <Eyebrow>Verified track record</Eyebrow>
            <H2 className="mt-5">We publish the losses too. <span className="text-[#7D8390]">Audit everything.</span></H2>
          </Reveal>
          <Reveal delay={0.05}>
            <p className="text-base leading-relaxed text-[#9DA3AE]">Every A-grade setup is published when it fires and resolved bar by bar on real candles{since ? `, since ${since}` : ""}. Nothing is deleted.</p>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="mt-12 grid grid-cols-2 gap-px border border-white/[0.07] bg-white/[0.07] lg:grid-cols-4" data-testid="verified-stats">
          {(stats || [["Live", "track record at verify"], ["Every", "setup published"], ["Bar by bar", "outcomes resolved"], ["Zero", "trades deleted"]]).map(([a, b]) => (
            <div key={b} className="bg-[#0A0B0D] p-6 md:p-8">
              <div className="font-display text-4xl font-semibold tracking-[-0.03em] text-[#ECEBE4] md:text-5xl">{a}</div>
              <div className="mt-2 font-mono text-[11px] uppercase tracking-[0.16em] text-[#5A6170]">{b}</div>
            </div>
          ))}
        </Reveal>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 font-mono text-[10px] uppercase tracking-[0.18em] text-[#5A6170]">
          <span>{live ? "Pulled live from verify.tradrquant.com" : "Live at verify.tradrquant.com"} · not financial advice · past results don't guarantee future results</span>
          <a href={VERIFY_URL} data-testid="verified-cta" className="inline-flex items-center gap-1.5 text-[#ECEBE4] hover:text-[#BACD1C]">Open verify <ArrowUpRight size={12} /></a>
        </div>
        <Reveal delay={0.12} className="mt-12"><Shot src="/of/verify.webp" alt="TradrQuant verification terminal" /></Reveal>
      </div>
    </section>
  );
};

export const Access = () => {
  const [open, setOpen] = useState(false);
  return (
    <section id="access" data-testid="access-section" className="border-t border-white/[0.06] bg-[#08090B] py-24 md:py-32">
      <span id="pricing" className="sr-only" />
      <span id="free" className="sr-only" />
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <Reveal className="max-w-3xl">
          <Eyebrow>Access</Eyebrow>
          <H2 className="mt-5">Free terminal now. <span className="text-[#7D8390]">Founder pricing if you sign up early.</span></H2>
        </Reveal>
        <div className="mt-14 grid gap-px border border-white/[0.07] bg-white/[0.07] md:grid-cols-2">
          <Reveal className="flex flex-col bg-[#08090B] p-8 md:p-10" data-testid="access-free">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#7D8390]">Live today · $0</p>
            <h3 className="mt-4 font-display text-3xl font-semibold tracking-[-0.03em] text-[#ECEBE4]">Public terminal</h3>
            <ul className="mt-6 flex-1 space-y-3 text-[15px] text-[#9DA3AE]">
              {["Order flow: footprint, Smart Tape, CVD", "Alpha Flow taker tape & Whale Radar", "TQ Pulse screener & TQ Regime", "Verify: the full public track record"].map((x) => <li key={x} className="flex gap-3"><span className="mt-2.5 h-1 w-1 shrink-0 bg-white/40" />{x}</li>)}
            </ul>
            <a href={ALPHA_URL} data-testid="access-free-cta" className="group mt-10 inline-flex items-center justify-center gap-2 bg-[#BACD1C] px-7 py-4 font-mono text-[13px] uppercase tracking-[0.14em] text-black transition-colors hover:bg-[#ECEBE4]">
              Open free terminal <ArrowUpRight size={17} />
            </a>
          </Reveal>
          <Reveal delay={0.08} className="flex flex-col bg-[#0B0C0F] p-8 md:p-10" data-testid="access-founder">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#FFB224]/90">Founder pricing · closes at launch</p>
            <h3 className="mt-4 font-display text-3xl font-semibold tracking-[-0.03em] text-[#ECEBE4]">Full TradrQuant terminal</h3>
            <ul className="mt-6 flex-1 space-y-3 text-[15px] text-[#9DA3AE]">
              {["A-grade trade setups from the 24/7 engine", "Deep research scorecards", "Impact-ranked breaking news", "Founder price locked in for as long as you stay"].map((x) => <li key={x} className="flex gap-3"><span className="mt-2.5 h-1 w-1 shrink-0 bg-white/40" />{x}</li>)}
            </ul>
            <button type="button" onClick={() => setOpen(true)} data-testid="access-founder-cta" className="group mt-10 inline-flex items-center justify-center gap-2 border border-white/25 px-7 py-4 font-mono text-[13px] uppercase tracking-[0.14em] text-[#ECEBE4] transition-[border-color,background-color] hover:border-white/50 hover:bg-white/[0.04]">
              Create free account <ArrowRight size={16} />
            </button>
            <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-[#5A6170]">No card required</p>
          </Reveal>
        </div>
      </div>
      <AnimatePresence>{open && <WaitlistModal open={open} onClose={() => setOpen(false)} />}</AnimatePresence>
    </section>
  );
};

export const ReferralTeaser = () => (
  <section data-testid="referral-teaser" className="border-t border-white/[0.06] bg-[#0A0B0D] py-20">
    <div className="mx-auto grid max-w-[1400px] items-center gap-8 px-6 md:grid-cols-[1.5fr_1fr] md:px-12">
      <Reveal>
        <Eyebrow>Referral program</Eyebrow>
        <h3 className="mt-5 font-display text-3xl font-semibold leading-[1.05] tracking-[-0.03em] text-[#ECEBE4] md:text-4xl">Bring your desk. <span className="text-[#7D8390]">Move up the queue, keep founder pricing, earn 20% recurring at launch.</span></h3>
      </Reveal>
      <Reveal delay={0.08} className="md:justify-self-end">
        <Link to="/referral" data-testid="referral-teaser-cta" className="group inline-flex items-center gap-2 border border-white/25 px-7 py-4 font-mono text-[13px] uppercase tracking-[0.14em] text-[#ECEBE4] transition-[border-color,background-color] hover:border-white/50 hover:bg-white/[0.04]">
          Get your referral link <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
        </Link>
      </Reveal>
    </div>
  </section>
);
